import * as Sentry from '@sentry/browser';

import { logSearchaniseProductListViewEvent, logSearchaniseProductSelectEvent } from '../analytics';
import {
  SortBy, SortOrder, SearchaniseResponse, fetchProducts, SearchaniseError,
} from '../services/searchaniseService';
import {
  CollectionTileEmbeds, SearchaniseTagSeparator,
  TagToSearchaniseFilterMapping,
} from './constants';
import {
  clearProducts, renderBackupCollectionsBlock,
  hideProductPlaceholders, renderProductPlaceholders, renderProducts, setupMobileSidebar,
} from './render';
import { selectors } from '../Constants';

const sortOptions: Record<string, {
  sortBy: SortBy;
  sortOrder: SortOrder;
}> = {
  'best-selling': { sortBy: 'sales_amount', sortOrder: 'desc' },
  'price-ascending': { sortBy: 'price', sortOrder: 'asc' },
  'price-descending': { sortBy: 'price', sortOrder: 'desc' },
  'created-ascending': { sortBy: 'created', sortOrder: 'asc' },
  'created-descending': { sortBy: 'created', sortOrder: 'desc' },
  'title-ascending': { sortBy: 'title', sortOrder: 'asc' },
  'title-descending': { sortBy: 'title', sortOrder: 'desc' },
};

type ApiParams = {
  sortBy: SortBy;
  sortOrder: SortOrder;
  startIndex: number;
} & Record<string, unknown>;

type CollectionData = {
  collectionHandle: string;
  collectionId: string;
  collectionTitle: string;
  collectionDefaultSort: string;
  currentTags: string[];
};

const collectionDataEl = document.querySelector('[data-collection-data-map]');
const collectionData: CollectionData = JSON.parse(collectionDataEl.innerHTML);

const collectionTileDataEl = document.querySelector('[data-tile-embeds]');
let collectionTileEmbeds: CollectionTileEmbeds = [];

if (collectionTileDataEl) {
  try {
    collectionTileEmbeds = JSON.parse(collectionTileDataEl.innerHTML);
  } catch (err) {
    console.error('Error parsing tile embeds', err);
    // Silently fail
  }
}

const mapTagsToFacets = () => {
  const currentTags = collectionData.currentTags || [];
  const facetMap: Record<string, string[]> = {};

  currentTags.forEach((tag) => {
    if (TagToSearchaniseFilterMapping[tag]) {
      const { facetId, filter } = TagToSearchaniseFilterMapping[tag];

      if (!facetMap[facetId]) {
        facetMap[facetId] = [];
      }

      facetMap[facetId].push(filter);
    }
  });

  return facetMap;
};

const firstLoadFacetMap = mapTagsToFacets();
const firstLoadApiFilters: Record<string, string> = {};

Object.keys(firstLoadFacetMap).forEach((facetKey) => {
  firstLoadApiFilters[`restrictBy[${facetKey}]`] = firstLoadFacetMap[facetKey].join('|');
});

const collectionHasManualSorting = collectionData.collectionDefaultSort === 'manual';

if (collectionHasManualSorting) {
  sortOptions.manual = {
    sortBy: `collection_${collectionData.collectionId}_position`,
    sortOrder: 'asc',
  };
}

const apiParams: ApiParams = {
  ...sortOptions[collectionData.collectionDefaultSort],
  startIndex: 0,
  ...firstLoadApiFilters,
};

let visibleItems: SearchaniseResponse['items'] = [];
const mapSearchaniseItemForAnalytics = (item: SearchaniseResponse['items'][0], productIndex: number) => {
  const { shopify_variants: variants } = item;
  const [firstVariant] = variants;
  const firstVariantTitle = Object.values(firstVariant.options || {}).join(' / ');

  return {
    product: {
      id: parseInt(item.product_id, 10),
      tags: item.tags.split(SearchaniseTagSeparator),
      title: item.title,
      vendor: item.vendor,
    },
    variant: {
      price: parseFloat(firstVariant.price),
      compare_at_price: parseFloat(firstVariant.list_price),
      available: firstVariant.available === '1',
      options: firstVariant.options as Record<string, string>,
      title: firstVariantTitle,
    },
    metadata: {
      collection: {
        title: collectionData.collectionTitle,
        id: parseInt(collectionData.collectionId, 10),
      },
      productIndex,
    },
  };
};

const logAnalyticsViewEvent = (items: SearchaniseResponse['items']) => {
  const mappedItems = items.map(
    (item, index) => mapSearchaniseItemForAnalytics(
      item, apiParams.startIndex + index,
    ),
  );

  logSearchaniseProductListViewEvent(mappedItems);
};

const expandAllFilters = () => {
  const sidebarFiltersEl = document.querySelector('.js-sidebar-filters');

  const filterHeaders = sidebarFiltersEl.querySelectorAll(selectors.expandable.header);

  filterHeaders.forEach((header: HTMLElement) => {
    header.click();
  });
};

const handleABTest = () => {
  // https://app.convert.com/accounts/10046042/projects/10046513/experiences/100477043/summary
  const convertQueue: {push: (obj: Record<string, any>) => void} = global.__conv_q || [];

  if (global.convert.currentData?.experiments['100477043']?.variation_id === '1004187273') {
    expandAllFilters();
  } else {
    convertQueue.push({
      what: 'addListener',
      params: {
        event: 'experience.variation_decided',
        // eslint-disable-next-line camelcase
        handler: ({ data: { variation_id: variationId } }: { data: { variation_id: string} }) => {
          if (variationId === '1004187273') {
            expandAllFilters();
          }
        },
      },
    });
  }
};

const fetchAndRender = async (firstLoad = false) => {
  const { startIndex } = apiParams;

  if (!firstLoad) {
    renderProductPlaceholders();
  }

  try {
    const response = await fetchProducts({
      collection: collectionData.collectionHandle,
      ...apiParams,
    });

    const successResponse = response as SearchaniseResponse;
    const { error } = response as SearchaniseError;

    if (error) {
      Sentry.captureException(new Error('Searchanise API call failed'), {
        extra: {
          error: response,
          apiParams,
        },
      });

      // If manual sort errors out due to misconfiguration, fallback to best selling sort
      if (error === 'SORT_ATTRIBUTE_WASNT_FOUND') {
        // eslint-disable-next-line no-use-before-define
        disableFeaturedSort();
        // eslint-disable-next-line no-use-before-define
        onChangeSort('best-selling');
      }

      return;
    }

    // console.log('response', response);

    const { currentItemCount, totalItems } = successResponse;

    if (currentItemCount === 0 && firstLoad) {
      renderBackupCollectionsBlock();
      return;
    }

    visibleItems = [...visibleItems, ...successResponse.items];

    const hasMore = (startIndex + currentItemCount) < totalItems;

    logAnalyticsViewEvent(successResponse.items);

    if (hasMore) {
      apiParams.startIndex += currentItemCount;
    }

    hideProductPlaceholders();

    if (firstLoad) {
      renderProducts({
        response: successResponse, collectionTileEmbeds, hasMore, firstLoadFacetMap,
      });
    } else {
      renderProducts({ response: successResponse, collectionTileEmbeds, hasMore });
    }
  } catch (error) {
    console.log('exception while rendering collection', error);

    Sentry.captureException(new Error('Searchanise API call or render failure'), {
      extra: {
        error,
        apiParams,
      },
    });
  }
};

const onChangeSort = (sortKey: string) => {
  apiParams.startIndex = 0;
  apiParams.sortBy = sortOptions[sortKey].sortBy;
  apiParams.sortOrder = sortOptions[sortKey].sortOrder;
  visibleItems = [];

  clearProducts();
  fetchAndRender();
};

const disableFeaturedSort = () => {
  $('.sort-by [value="manual"]').remove();
  const mobileSortByEl = document.querySelector('.js-sort-list');
  mobileSortByEl.querySelector('[data-value="manual"]').remove();
};

const setupSorting = () => {
  $(() => {
    if (!collectionHasManualSorting) {
      disableFeaturedSort();
    }

    $('.sort-by')
      .val(collectionData.collectionDefaultSort)
      .on('change', () => {
        const sortKey = $('.sort-by').val() as string;
        onChangeSort(sortKey);
      });

    $('.js-sort-by').selectize({
      create: false,
      persist: false,
    });
  });

  const mobileSortByEl = document.querySelector('.js-sort-list');

  mobileSortByEl.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const sortKey = target.getAttribute('data-value');
    if (!sortKey) { return; }
    onChangeSort(sortKey);
  });
};

const onChangeFilters = (e: Event) => {
  const target = e.target as HTMLElement;

  if (!target.classList.contains('js-filter-checkbox')) {
    return;
  }

  const filtersWrapperEl = target.closest('.js-filter-item__content');
  const facetId = filtersWrapperEl.getAttribute('data-facet-id');
  const filterKey = `restrictBy[${facetId}]`;
  const selectedFacetEls = filtersWrapperEl.querySelectorAll('.js-filter-checkbox:checked');
  const selectedFacetValues = [...selectedFacetEls].map((f) => f.getAttribute('data-level'));

  if (selectedFacetValues.length) {
    apiParams[filterKey] = selectedFacetValues.join('|');
  } else {
    delete apiParams[filterKey];
  }

  apiParams.startIndex = 0;
  visibleItems = [];

  clearProducts();
  fetchAndRender();
};

const handleCardClick = (e: CustomEvent<{productId: string }>) => {
  const { productId } = e.detail;

  const itemIndex = visibleItems.findIndex((i) => i.product_id === productId);

  if (itemIndex === -1) {
    return;
  }
  const item = visibleItems[itemIndex];

  logSearchaniseProductSelectEvent(mapSearchaniseItemForAnalytics(item, itemIndex));
};

const init = async () => {
  setupSorting();
  const loadMoreEl = document.querySelector('.js-collections-load-more');
  const sidebarFiltersEl = document.querySelector('.js-sidebar-filters');

  loadMoreEl.addEventListener('click', () => fetchAndRender());
  fetchAndRender(true);
  sidebarFiltersEl.addEventListener('change', onChangeFilters);
  document.addEventListener('searchanise-card-clicked', handleCardClick);
  setupMobileSidebar();
};

init();
